import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Zap, CheckSquare, ChevronRight } from "lucide-react";
import SectionContainer from "../components/SectionContainer";

function BeforeMockup() {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-2 w-2 rounded-full bg-[#0092B4]" />
        <span className="text-[11px] font-semibold uppercase tracking-widest text-[#0092B4]">Pre-Call Brief</span>
        <span className="ml-auto text-[11px] text-neutral-400">Generating…</span>
      </div>
      <p className="text-[15px] font-semibold text-neutral-900 mb-3">Acme Corp · Discovery Call</p>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { label: "Opportunity", value: "$240K ARR" },
          { label: "Stage",       value: "Tech Eval"  },
          { label: "Close date",  value: "Aug 31"     },
        ].map(cell => (
          <div key={cell.label} className="rounded-lg bg-neutral-50 border border-neutral-100 p-2">
            <p className="text-[9px] uppercase tracking-wide text-neutral-400">{cell.label}</p>
            <p className="mt-0.5 text-[12px] font-semibold text-neutral-800">{cell.value}</p>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        {[
          "Last call: pricing concern raised — address proactively",
          "David L. (CFO) now on the invite — prepare ROI case",
          "Competing tools evaluated — prepare differentiation on execution depth",
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-2 text-[12px] text-neutral-600">
            <ChevronRight className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#0092B4]" strokeWidth={2.5} />
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

function DuringMockup() {
  const signals = [
    { type: "emerald", label: "Buying signal", text: "Budget confirmed for Q3 close" },
    { type: "amber",   label: "Objection",     text: '"August deadline is non-negotiable"' },
    { type: "blue",    label: "Stakeholder",   text: "CFO joined — pivot to financial ROI" },
  ];
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm space-y-3">
      <div className="flex items-center gap-2">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
        </span>
        <span className="text-[11px] font-semibold uppercase tracking-widest text-emerald-600">Live · 0:28:04</span>
        <span className="ml-auto text-[11px] text-neutral-400">Acme Corp</span>
      </div>
      {signals.map((s, i) => (
        <div key={i} className={`rounded-xl border p-3 ${
          s.type === "emerald" ? "border-emerald-200/60 bg-emerald-50/50" :
          s.type === "amber"   ? "border-amber-200/60  bg-amber-50/50"   :
                                 "border-blue-200/60   bg-blue-50/50"
        }`}>
          <div className="flex items-center gap-1.5 mb-1">
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
              s.type === "emerald" ? "bg-emerald-500" :
              s.type === "amber"   ? "bg-amber-500"   : "bg-blue-500"
            }`} />
            <p className={`text-[9.5px] font-semibold uppercase tracking-widest ${
              s.type === "emerald" ? "text-emerald-700" :
              s.type === "amber"   ? "text-amber-700"   : "text-blue-700"
            }`}>{s.label}</p>
          </div>
          <p className="text-[12px] text-neutral-700 leading-snug">{s.text}</p>
        </div>
      ))}
    </div>
  );
}

function AfterMockup() {
  const actions = [
    { label: "Salesforce opportunity updated",     tool: "Salesforce", done: true  },
    { label: "Follow-up email drafted to Sarah K.", tool: "Gmail",      done: true  },
    { label: "Jira onboarding ticket created",      tool: "Jira",       done: true  },
    { label: "Summary posted to #sales-team",       tool: "Slack",      done: false },
  ];
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-neutral-500">Post-Call Execution</span>
        <span className="ml-auto text-[11px] text-[#0092B4] font-medium">3 / 4 done</span>
      </div>
      <div className="space-y-2.5">
        {actions.map((a, i) => (
          <div key={i} className="flex items-center gap-2.5">
            <div className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 ${
              a.done ? "bg-emerald-100" : "bg-[#0092B4]/10"
            }`}>
              {a.done
                ? <svg className="h-3 w-3 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                : <span className="h-2 w-2 rounded-full bg-[#0092B4] animate-pulse" />
              }
            </div>
            <span className={`text-[12px] flex-1 ${a.done ? "text-neutral-600" : "text-[#0092B4]"}`}>{a.label}</span>
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded uppercase tracking-wide ${
              a.done ? "bg-neutral-100 text-neutral-500" : "bg-[#0092B4]/10 text-[#0092B4]"
            }`}>{a.tool}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const PHASES = [
  {
    id: "before",
    label: "Before the Call",
    icon: FileText,
    tag: "Preparation",
    headline: "Enter every meeting prepared.",
    description:
      "Shadow pulls account history, CRM data, prior meeting notes, email threads, and internal knowledge to build a rep-specific brief before every call.",
    points: [
      "Account history, opportunity stage, and open action items from prior meetings",
      "Key contacts, their roles, and relevant background",
      "Recommended talking points and discovery questions",
      "CRM signals and pipeline health at a glance",
    ],
    Mockup: BeforeMockup,
  },
  {
    id: "during",
    label: "During the Call",
    icon: Zap,
    tag: "Real-Time",
    headline: "Act on signals the moment they surface.",
    description:
      "Shadow monitors the call in real time, detecting the signals that move deals — objections, buying signals, stakeholder changes, competitor mentions, and more.",
    points: [
      "Buying signal and objection detection as they happen",
      "Stakeholder identification when new participants join",
      "Suggested responses to competitor mentions and pricing pushback",
      "Next-best-action prompts as risks and opportunities emerge",
    ],
    Mockup: DuringMockup,
  },
  {
    id: "after",
    label: "After the Call",
    icon: CheckSquare,
    tag: "Execution",
    headline: "No follow-up falls through the cracks.",
    description:
      "Shadow auto-generates summaries, updates your CRM, drafts personalized follow-ups, creates tasks in Jira or Linear, posts to Slack, and files notes in Notion.",
    points: [
      "CRM updated with meeting context, stage change, and next steps",
      "Personalized follow-up email drafted in your writing style",
      "Action items created in Jira, Linear, or your task tool of choice",
      "Meeting summary posted to the right Slack channel",
    ],
    Mockup: AfterMockup,
  },
];

export default function PlatformWorkflowSection() {
  const [active, setActive] = useState("before");
  const phase = PHASES.find(p => p.id === active);
  const { Mockup } = phase;

  return (
    <SectionContainer id="platform" className="py-20 sm:py-28">
      <div className="mx-auto max-w-3xl text-center mb-14">
        {/* No section-label pill — headline carries the weight */}
        <h2 className="text-[clamp(1.8rem,4vw,2.85rem)] font-bold tracking-tight text-neutral-900">
          Preparation before.{" "}
          <span className="text-[#0092B4]">Live intelligence during.</span>{" "}
          Execution after.
        </h2>
        <p className="mt-4 text-base text-neutral-500 max-w-xl mx-auto">
          Shadow is active before, during, and after every call — so nothing falls through between meetings.
        </p>
      </div>

      {/* Premium segmented-control tab switcher */}
      <div className="flex justify-center mb-12">
        <div className="inline-flex items-center bg-neutral-100 rounded-full p-1 gap-0.5">
          {PHASES.map(p => {
            const Icon = p.icon;
            return (
              <button
                key={p.id}
                onClick={() => setActive(p.id)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 ${
                  active === p.id
                    ? "bg-white text-neutral-900 shadow-sm shadow-black/10"
                    : "text-neutral-500 hover:text-neutral-700"
                }`}
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Phase content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16"
        >
          {/* Text */}
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#0092B4]/10 border border-[#0092B4]/20 px-3 py-1 mb-5">
              <span className="text-[11px] font-semibold uppercase tracking-widest text-[#0092B4]">{phase.tag}</span>
            </div>
            <h3 className="text-[1.65rem] font-bold tracking-tight text-neutral-900 leading-tight mb-4">
              {phase.headline}
            </h3>
            <p className="text-[15px] text-neutral-500 leading-relaxed mb-6">{phase.description}</p>
            <ul className="space-y-3">
              {phase.points.map((pt, i) => (
                <li key={i} className="flex items-start gap-3 text-[14px] text-neutral-700">
                  <div className="mt-0.5 h-5 w-5 rounded-full bg-[#0092B4]/10 flex items-center justify-center shrink-0">
                    <svg className="h-3 w-3 text-[#0092B4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  {pt}
                </li>
              ))}
            </ul>
          </div>

          {/* Mockup */}
          <div>
            <Mockup />
          </div>
        </motion.div>
      </AnimatePresence>
    </SectionContainer>
  );
}
